<script lang="ts">
  import Button from '../ui/Button.svelte';
  import Card from '../ui/Card.svelte';
  import LobbyRoster from './LobbyRoster.svelte';
  import ThemeIcon from '../themes/ThemeIcon.svelte';
  import type { PlayerView, TurnProgress } from '../../shared/protocol';

  /**
   * The turn-taking room between turns: who has played this round, and the Play button
   * that starts this browser's own timed turn on the shared board.
   */
  interface Props {
    roomCode: string;
    round: number;
    roundDuration: number;
    players: readonly Readonly<PlayerView>[];
    meId: string;
    /** Only the role holder removes players. */
    canKick: boolean;
    spectator: boolean;
    finished: boolean;
    hasResults: boolean;
    /** Completed Turns checkpoint: any member may open the next round. */
    completed: boolean;
    beginTurn: () => void;
    nextRound: () => void;
    viewResults: () => void;
    /** Full invite URL for the title tooltip; the button shows the room code. */
    inviteUrl: string;
    shareRoomLink: () => void;
    leaveRoom: () => void;
    kickPlayer: (id: string) => void;
  }
  let {
    roomCode,
    round,
    roundDuration,
    players,
    meId,
    canKick,
    spectator,
    finished,
    hasResults,
    completed,
    beginTurn,
    nextRound,
    viewResults,
    inviteUrl,
    shareRoomLink,
    leaveRoom,
    kickPlayer,
  }: Props = $props();

  const TURN_LABEL: Record<TurnProgress, string> = {
    waiting: 'not yet played',
    playing: 'playing now',
    finished: 'done',
  };
  const pending = $derived(
    players.filter((player) => player.turn === 'waiting' || player.turn === 'playing'),
  );
</script>

<section id="screen-turn-lobby" class="screen">
  <div class="turn-header">
    <h1>Round {round}</h1>
    <Button
      size="small"
      class="lobby-share"
      aria-label="Share room invite"
      title={inviteUrl}
      onclick={shareRoomLink}><span class="code">{roomCode}</span><ThemeIcon name="share" /></Button
    >
  </div>
  <Card>
    <p class="hint">
      {#if completed}
        Round complete. Anyone can start the next round, or review the recap first.
      {:else if spectator}
        You joined during this round and play from the next one.
      {:else if finished}
        You're done. Once everyone has played, anyone can start the next round.
      {:else}
        Everyone plays the same board on their own time. You have {roundDuration} seconds once you press Play.
      {/if}
    </p>
    <LobbyRoster {players} {meId} {canKick} {kickPlayer} presence={false}>
      {#snippet detail(player)}
        <span class="turn" class:done={player.turn === 'finished'}
          >{player.spectator ? 'next round' : player.turn ? TURN_LABEL[player.turn] : ''}</span
        >
      {/snippet}
    </LobbyRoster>
    {#if !spectator && !finished && !completed}
      <Button variant="primary" size="big" full onclick={beginTurn}>Play Your Turn</Button>
    {:else if !completed && pending.length}
      <p class="status">waiting for {pending.map((player) => player.name).join(', ')}</p>
    {/if}
    {#if completed}<Button variant="primary" size="big" full onclick={nextRound}>Next Round</Button>{/if}
    {#if hasResults}<Button size="big" full onclick={viewResults}>Last Round Results</Button>{/if}
    <Button size="big" full onclick={leaveRoom}>Leave Room</Button>
  </Card>
</section>

<style>
  #screen-turn-lobby {
    gap: 14px;
  }
  .turn-header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
  }
  .turn-header h1 {
    flex-shrink: 0;
    font-size: 36px;
    letter-spacing: -1px;
  }
  .turn {
    font-size: 11px;
    color: var(--theme-muted-text);
  }
  .turn.done {
    color: var(--theme-accent);
    font-weight: 700;
  }
</style>
